import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Card from '@/components/wrapped/Card';
import Screen from '@/karehero-app/explore/Screen';
import { CLAIMS_APP } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

// Section B — what the app becomes once eligibility is known. Both frames
// lean on the wrapped Card rather than restyling surfaces per screen, so the
// question they raise is about structure (are claims the spine?) and not
// about how a card looks.

// B1 — home rebuilt around claims. Each claim carries its own state, and the
// diary prompt sits at the bottom as the open question of where "tell us
// what's changed" lives once claims are the spine.
export const B1ClaimsHub = () => {
  const c = CLAIMS_APP.b1;
  return (
    <Screen>
      <Stack sx={{ pt: 2, pb: 3 }} spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant='body2' color='text.secondary'>
            {c.greeting}
          </Typography>
          <Typography variant='h2'>{c.lead}</Typography>
        </Stack>

        <Stack spacing={1.5}>
          {c.claims.map((claim) => (
            <Card
              key={claim.name}
              title={claim.name}
              variant='compact'
              subtitle={claim.detail}
              chip={<Chip size='small' label={claim.state} />}
              onClick={() => {}}
            />
          ))}
        </Stack>

        <Divider />

        <Paper variant='outlined'>
          <Stack
            sx={{ alignItems: 'flex-start', p: 2 }}
            direction='row'
            spacing={2}
          >
            <Box sx={{ color: 'custom.coral.600', display: 'flex', pt: '2px' }}>
              <Icon icon={'message' as never} fontSize={16} />
            </Box>
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
              <Typography variant='h5'>{c.diaryPrompt}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {c.diaryHint}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Screen>
  );
};
B1ClaimsHub.displayName = 'B1ClaimsHub';

const SECTION_META = {
  done: { icon: 'check-circle', color: 'custom.limeGreen.800', label: 'Done' },
  open: { icon: 'circle', color: 'custom.neutral.300', label: 'Not started' },
  later: { icon: 'clock', color: 'custom.warmOrange.500', label: 'Later' },
} as const;

// B2 — one claim opened up, part-assembled. The 'later' row is the frame's
// point of tension: it's a deferred item from the capture flow, and it needs
// to read as parked rather than as unfinished homework.
export const B2SingleClaim = () => {
  const c = CLAIMS_APP.b2;
  return (
    <Screen
      title={c.claim}
      back
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Typography variant='body2' color='text.secondary'>
          {c.progressLabel}
        </Typography>

        <Stack divider={<Divider />}>
          {c.sections.map((s) => {
            const meta = SECTION_META[s.state];
            return (
              <Stack
                sx={{ alignItems: 'flex-start', py: 1.75 }}
                key={s.label}
                direction='row'
                spacing={2}
              >
                <Box sx={{ color: meta.color, display: 'flex', pt: '3px' }}>
                  <Icon icon={meta.icon as never} fontSize={15} />
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                  <Typography variant='h5'>{s.label}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {s.detail}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Screen>
  );
};
B2SingleClaim.displayName = 'B2SingleClaim';
